import { ChevronDown, Mic, MicOff, Video, VideoOff, Check } from 'lucide-react';
import { cn } from '../lib/utils';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from './dropdown-menu';
import type { MediaDevice } from '../types/media.types';

export interface DeviceSelectorProps {
  /** Icon to display */
  icon: 'mic' | 'video';
  /** Whether the device is enabled */
  isEnabled: boolean;
  /** Available devices */
  devices: MediaDevice[];
  /** Currently selected device ID */
  selectedDeviceId: string | null;
  /** Callback when device is selected */
  onDeviceSelect: (deviceId: string) => void;
  /** Callback when toggle is clicked */
  onToggle: () => void;
  /** Custom class name */
  className?: string;
}

const iconMap = {
  mic: { enabled: Mic, disabled: MicOff },
  video: { enabled: Video, disabled: VideoOff },
} as const;

export const DeviceSelector = ({
  icon,
  isEnabled,
  devices,
  selectedDeviceId,
  onDeviceSelect,
  onToggle,
  className,
}: DeviceSelectorProps) => {
  const icons = iconMap[icon];
  const Icon = isEnabled ? icons.enabled : icons.disabled;
  const deviceLabel = icon === 'mic' ? 'Microphone' : 'Camera';

  return (
    <div
      className={cn(
        'flex items-center rounded-xl border border-slate-200 bg-slate-100',
        className
      )}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex h-12 w-8 items-center justify-center rounded-l-xl transition-colors hover:bg-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-orange-500"
            aria-label={`Select ${deviceLabel}`}
          >
            <ChevronDown className="h-4 w-4 text-slate-500" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuLabel>{deviceLabel}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {devices.length === 0 ? (
            <DropdownMenuItem disabled>
              No {deviceLabel.toLowerCase()} detected
            </DropdownMenuItem>
          ) : (
            devices.map((device) => (
              <DropdownMenuItem
                key={device.deviceId}
                onClick={() => onDeviceSelect(device.deviceId)}
                className="flex items-center justify-between"
              >
                <span className="truncate">{device.label || `${deviceLabel} ${device.deviceId.slice(0, 8)}`}</span>
                {selectedDeviceId === device.deviceId && (
                  <Check className="ml-2 h-4 w-4 flex-shrink-0 text-orange-500" />
                )}
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <button
        type="button"
        onClick={onToggle}
        className={cn(
          'flex h-12 w-12 items-center justify-center rounded-r-xl transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-orange-500',
          isEnabled
            ? 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            : 'bg-slate-200 text-slate-400'
        )}
        aria-label={`Toggle ${deviceLabel}`}
      >
        <Icon className="h-5 w-5" />
      </button>
    </div>
  );
};
